/**
 * Economy — stockpiled resources, income from buildings, upkeep for units.
 * Driven by data: building income lives in each BuildingDef's `extra`
 * (`income` / `production` as resource-id -> per-second maps).
 */
import type { GameDB } from "../data/gamedb";
import type { ResourceDef } from "../data/types";
import type { PlacedBuilding } from "./buildings";

export class Economy {
  stocks = new Map<string, number>();
  /** Per-second net rates, recomputed each tick for the HUD. */
  rates = new Map<string, number>();

  constructor(
    private readonly db: GameDB,
    initial: Record<string, number>,
  ) {
    for (const [id, amount] of Object.entries(initial)) {
      this.stocks.set(id, amount);
    }
  }

  resourceDefs(): ResourceDef[] {
    return [...this.db.resources.values()].sort((a, b) => a.order - b.order);
  }

  canAfford(cost: Record<string, number>): boolean {
    return Object.entries(cost).every(
      ([id, amount]) => (this.stocks.get(id) ?? 0) >= amount,
    );
  }

  pay(cost: Record<string, number>): boolean {
    if (!this.canAfford(cost)) return false;
    for (const [id, amount] of Object.entries(cost)) {
      this.stocks.set(id, (this.stocks.get(id) ?? 0) - amount);
    }
    return true;
  }

  /** Add resources (worker drop-offs, etc.). */
  add(id: string, amount: number): void {
    this.stocks.set(id, Math.max(0, (this.stocks.get(id) ?? 0) + amount));
  }

  /** Replace all stocks (save/load). */
  setStocks(stocks: Record<string, number>): void {
    this.stocks = new Map(Object.entries(stocks));
  }

  stockRecord(): Record<string, number> {
    return Object.fromEntries(this.stocks);
  }

  tick(
    dt: number,
    buildings: PlacedBuilding[],
    extraUpkeep: Record<string, number> = {},
    /** Multiplicative production bonuses by resource (tech gather_rate). */
    prodMult: Record<string, number> = {},
    /** Upkeep multiplier (tech upkeep_cost). */
    upkeepMult = 1,
    /** Flat per-second income adds by resource (tech income effects). */
    flatIncome: Record<string, number> = {},
  ): void {
    const delta = new Map<string, number>();
    const add = (id: string, v: number) =>
      delta.set(id, (delta.get(id) ?? 0) + v);
    for (const b of buildings) {
      if (b.state !== "active") continue;
      const prod =
        ((b.def.extra["income"] ??
          b.def.extra["production"]) as Record<string, number>) ?? {};
      const upkeep = (b.def.extra["upkeep"] as Record<string, number>) ?? {};
      for (const [id, rate] of Object.entries(prod)) {
        add(id, rate * (prodMult[id] ?? 1) * b.efficiency * dt);
      }
      for (const [id, rate] of Object.entries(upkeep)) {
        add(id, -rate * upkeepMult * b.efficiency * dt);
      }
    }
    for (const [id, rate] of Object.entries(extraUpkeep)) add(id, -rate * dt);
    for (const [id, rate] of Object.entries(flatIncome)) add(id, rate * dt);
    for (const [id, d] of delta) {
      this.stocks.set(id, Math.max(0, (this.stocks.get(id) ?? 0) + d));
      this.rates.set(id, (this.rates.get(id) ?? 0) * 0.95 + (d / Math.max(dt, 1e-6)) * 0.05);
    }
  }

  /** Per-second rates as a plain record (for echelon requirement checks). */
  rateRecord(): Record<string, number> {
    return Object.fromEntries(this.rates);
  }
}
